import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { PageHeader, PageShell } from '~/components/layout';
import { FailedSections, StatTile, StatTileSkeleton, StatsRow } from '~/components/dashboard';
import { GUARDS } from '~/app/access';
import { routes } from '~/app/routes';
import { formatLocalTime } from '~/lib/datetime';
import { formatPercent } from '~/lib/format';
import { backlogSeverity, capacitySeverity, successSeverity } from '~/lib/severity';
import { getAccess } from '~/modules/access';
import { queryErrorMessage } from '~/modules/query';
import { useListPlacementsQuery } from '~/modules/query/ai/placements';
import { useGetUsageQuery, useGetUsageStatsQuery } from '~/modules/query/ai/settings';
import {
  useGetDashboardFlagsQuery,
  useGetDashboardRequestsQuery,
  useGetDashboardUpdatesQuery,
  useListEnvironmentsQuery,
} from '~/modules/query/featureFlags';
import { useGetHomeStatsQuery } from '~/modules/query/home';
import { useGetVideoStatsQuery } from '~/modules/query/listingVideos';
import { useGetMlsStatsQuery } from '~/modules/query/mls';
import { useGetAgreementExpiringQuery } from '~/modules/query/mlsAgreements';
import type { ExpiringGroups } from '~/components/pages/mls-agreements/types';
import { HOME_STAT_TITLES, toStatValue } from '~/components/Constants/homeConstants';
import { LIST_OF_CARDS } from '~/components/Constants/videodashboardConstants';

const AI_DAYS = 30;

interface Failure {
  name: string;
  error: unknown;
  refetch: () => void;
}

const failure = (
  name: string,
  query: { isError: boolean; error?: unknown; refetch: () => void },
): Failure[] => (query.isError ? [{ name, error: query.error, refetch: query.refetch }] : []);

/** One area's tile row under a small heading that links to the area's own overview. */
function AreaRow({
  title,
  overviewHref,
  children,
}: {
  title: string;
  overviewHref: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-gray-500 text-xs uppercase leading-5 dark:text-gray-400">{title}</h2>
        <Link
          to={overviewHref}
          className="focus-ring rounded text-brand-500 text-sm hover:underline dark:text-brand-400"
        >
          View overview
        </Link>
      </div>
      <StatsRow ariaLabel={title}>{children}</StatsRow>
    </div>
  );
}

const VIDEO_TILES = ['countRendered', 'countFailed', 'numberOfQueuedJobs'] as const;

function useVideoTiles() {
  const stats = useGetVideoStatsQuery();

  const row = (
    <AreaRow title="Listing videos" overviewHref={routes.listingVideos}>
      {stats.isLoading ? (
        <>
          <StatTileSkeleton />
          {VIDEO_TILES.map((key) => (
            <StatTileSkeleton key={key} />
          ))}
        </>
      ) : (
        <>
          <StatTile
            label={LIST_OF_CARDS.lastProcessedTimestamp}
            value={formatLocalTime(stats.data?.lastProcessedTimestamp as string | undefined)}
            href={routes.listingVideos}
          />
          {VIDEO_TILES.map((key) => (
            <StatTile
              key={key}
              label={LIST_OF_CARDS[key]}
              value={toStatValue(stats.data?.[key])}
              href={routes.listingVideos}
            />
          ))}
        </>
      )}
    </AreaRow>
  );

  return { row, failed: failure('video counters', stats) };
}

function useAiTiles(enabled: boolean) {
  const range = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (AI_DAYS - 1));
    return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
  }, []);
  const usage = useGetUsageQuery(undefined, { skip: !enabled });
  const stats = useGetUsageStatsQuery(range, { skip: !enabled });
  const placements = useListPlacementsQuery(undefined, { skip: !enabled });

  const summary = stats.data?.summary;
  const hasRequests = !!summary && summary.successCount + summary.failureCount > 0;
  const all = placements.data?.placements ?? [];
  const active = all.filter((placement) => placement.status === 'active').length;

  const row = (
    <AreaRow title="AI" overviewHref={routes.ai}>
      {usage.isLoading ? (
        <>
          <StatTileSkeleton />
          <StatTileSkeleton />
        </>
      ) : (
        <>
          <StatTile
            label="Daily token usage"
            value={usage.data ? formatPercent(usage.data.daily.percentUsed) : undefined}
            severity={usage.data ? capacitySeverity(usage.data.daily.percentUsed) : undefined}
            href={routes.ai}
          />
          <StatTile
            label="Monthly token usage"
            value={usage.data ? formatPercent(usage.data.monthly.percentUsed) : undefined}
            severity={usage.data ? capacitySeverity(usage.data.monthly.percentUsed) : undefined}
            href={routes.ai}
          />
        </>
      )}
      {stats.isLoading ? (
        <StatTileSkeleton />
      ) : (
        <StatTile
          label={`Success rate (${AI_DAYS}d)`}
          value={summary ? formatPercent(summary.successRate) : undefined}
          severity={hasRequests ? successSeverity(summary.successRate) : undefined}
          href={routes.aiAnalytics}
        />
      )}
      {placements.isLoading ? (
        <StatTileSkeleton />
      ) : (
        <StatTile
          label="Placements active"
          value={placements.data ? `${active} of ${all.length}` : undefined}
          href={routes.aiPlacements}
        />
      )}
    </AreaRow>
  );

  return {
    row: enabled ? row : null,
    failed: [
      ...failure('AI token usage', usage),
      ...failure('AI success rate', stats),
      ...failure('AI placements', placements),
    ],
  };
}

/** The feature flag totals for the first environment, the same default the Feature flags overview opens on. */
function useFeatureFlagTiles(enabled: boolean) {
  const environments = useListEnvironmentsQuery(undefined, { skip: !enabled });
  const environment = environments.data?.[0];
  const args = { environment: environment?.key ?? '' };
  const flags = useGetDashboardFlagsQuery(args, { skip: !environment });
  const updates = useGetDashboardUpdatesQuery(args, { skip: !environment });
  const requests = useGetDashboardRequestsQuery({}, { skip: !enabled });
  const awaiting = requests.data?.totals.awaitingReview ?? 0;

  const row = environment ? (
    <AreaRow title={`Feature flags · ${environment.name}`} overviewHref={routes.featureFlags}>
      {flags.isLoading ? (
        <>
          <StatTileSkeleton />
          <StatTileSkeleton />
        </>
      ) : (
        <>
          <StatTile
            label="Known users"
            value={flags.data?.knownUsers}
            href={routes.featureFlagsSection('users')}
          />
          <StatTile
            label="Active flags"
            value={flags.data?.flags.length}
            href={routes.featureFlagsSection('flags')}
          />
        </>
      )}
      {updates.isLoading ? (
        <StatTileSkeleton />
      ) : (
        <StatTile
          label="Updates live"
          value={updates.data?.updates.length}
          href={routes.featureFlagsSection('updates')}
        />
      )}
      {requests.isLoading ? (
        <StatTileSkeleton />
      ) : (
        <StatTile
          label="Requests to review"
          value={requests.data?.totals.awaitingReview}
          href={`${routes.featureFlagsSection('requests')}?status=submitted`}
          severity={backlogSeverity(awaiting)}
        />
      )}
    </AreaRow>
  ) : null;

  return {
    row,
    failed: [
      ...failure('feature flag environments', environments),
      ...failure('flag adoption', flags),
      ...failure('product updates', updates),
      ...failure('feature requests', requests),
    ],
  };
}

export const Index = () => {
  const mlsStats = useGetMlsStatsQuery();
  const customerStats = useGetHomeStatsQuery({});
  const transactionStats = useGetHomeStatsQuery({ transactions: true });
  const canUseMls = GUARDS.mlsAgreements.allow(getAccess());
  const expiring = useGetAgreementExpiringQuery(undefined, { skip: !canUseMls });
  // The groups are exclusive (d7 = 0–7 days, d30 = 8–30), so "within 30 days" is both.
  const expiringGroups = expiring.data as ExpiringGroups | undefined;
  const expiringSoon = expiringGroups
    ? (expiringGroups.d7?.length ?? 0) + (expiringGroups.d30?.length ?? 0)
    : undefined;
  const videos = useVideoTiles();
  const ai = useAiTiles(GUARDS.ai.allow(getAccess()));
  const featureFlags = useFeatureFlagTiles(GUARDS.featureFlags.allow(getAccess()));

  // MLS stats come back as { nTot }, /customers/stats as a bare number; each tile waits on its own query.
  const tiles = [
    { key: 'nTot', query: mlsStats, value: mlsStats.data?.nTot },
    { key: 'ofCustomers', query: customerStats, value: customerStats.data },
    { key: 'ofCustomerTransaction', query: transactionStats, value: transactionStats.data },
  ];
  const failed = [
    ...tiles.flatMap((tile) => failure(HOME_STAT_TITLES[tile.key], tile.query)),
    ...(canUseMls ? failure('expiring memberships', expiring) : []),
    ...videos.failed,
    ...ai.failed,
    ...featureFlags.failed,
  ];

  return (
    <PageShell>
      <PageHeader title="Dashboard" description="Toolbox operations overview." />
      <FailedSections
        failed={failed.map((entry) => entry.name)}
        message={failed[0] ? queryErrorMessage(failed[0].error) : undefined}
        onRetry={() => failed.forEach((entry) => entry.refetch())}
        testId="dashboard"
      />
      <StatsRow ariaLabel="Overview">
        {tiles.map((tile) =>
          tile.query.isLoading ? (
            <StatTileSkeleton key={tile.key} />
          ) : (
            <StatTile
              key={tile.key}
              label={HOME_STAT_TITLES[tile.key]}
              value={toStatValue(tile.value)}
            />
          ),
        )}
        {!canUseMls ? null : expiring.isLoading ? (
          <StatTileSkeleton />
        ) : (
          <StatTile
            label="Memberships expiring in 30 days"
            value={expiringSoon}
            severity={expiringSoon !== undefined ? backlogSeverity(expiringSoon) : undefined}
            href={`${routes.mlsAgreements}?tab=expiring`}
          />
        )}
      </StatsRow>
      {videos.row}
      {ai.row}
      {featureFlags.row}
    </PageShell>
  );
};
